// `extension-alert-candidates-add-to-job` plus the `form/` it wraps — "Add <someone> to
// Bullhorn job", a job autocomplete, a status dropdown and an orange Add.
//
// ⚠️ THE ADD BUTTON WRITES INTO A CUSTOMER'S ATS. `addToAtsJobAsync` enqueues a real record
// against a real job in the tenant's Bullhorn/Vincere/JobDiva. Nothing here fires it on mount,
// on focus, or on a default — only the click, and only with both fields chosen.
import { createElement, useState, type ReactElement } from 'react'

/** `IExtensionJobSearchDto`. */
export interface AtsJob {
  id: number
  title: string
  internalTitle?: string | null
  employerName?: string | null
}

/** `IExtensionRawJobStatusItem` — `status` submits, `statusText` displays. */
export interface AtsJobStatus {
  status: string
  statusText: string
}

/** Which ATS the tenant runs, for the two behaviours that key off it. */
export interface AddToJobAtsOptions {
  atsName?: string
  /**
   * `ctrl.atsLabel` — the heading's "<ATS> job" (5cfd3ef899). `atsName` keeps the JobDiva and
   * locked-status rules. Defaults to `atsName`.
   */
  atsLabel?: string
  /** `careero.atsHasJobCode` — adds `internalTitle` to the autocomplete row. */
  atsHasJobCode?: boolean
  /** `careero.atsHasConcreteEmployers` — adds " - <employer>" to both texts. */
  atsHasConcreteEmployers?: boolean
}

/** `getJobDisplayText` — what the INPUT shows once a job is chosen. */
export function jobDisplayText(job: AtsJob | undefined | null, options: AddToJobAtsOptions = {}): string {
  if (job === undefined || job === null) return ''
  return `${job.title}${options.atsHasConcreteEmployers === true ? ` - ${job.employerName ?? ''}` : ''}`
}

/**
 * `getJobAutocompleteText` — what a SUGGESTION ROW shows.
 *
 * ⚠️ IT IS NOT `getJobDisplayText`. The suggestion adds the ATS's own job code between the
 * title and the employer when the tenant has one; the input, once chosen, drops it again. Using
 * one function for both either hides the code a recruiter picks by, or leaves it in the field.
 */
export function jobAutocompleteText(job: AtsJob, options: AddToJobAtsOptions = {}): string {
  const code = options.atsHasJobCode === true ? ` ${job.internalTitle ?? ''}` : ''
  const employer = options.atsHasConcreteEmployers === true ? ` - ${job.employerName ?? ''}` : ''
  return `${job.title}${code}${employer}`
}

/**
 * `getCandidateCTAText` — the words between "Add" and "to".
 *
 * ⚠️ NOT THE EXPORT PANEL'S FUNCTION OF THE SAME UPSTREAM NAME. Both components declare a
 * private `getCandidateCTAText`, and they disagree on the case that matters: a single UNNAMED
 * candidate reads as an empty string here and falls back to the plural there. Exporting both as
 * `candidateCtaText` made `@woo-ext/ui` re-export one name from two modules, which TypeScript
 * rejects outright — so the name carries the panel.
 */
export function addToJobCtaText(checked: ReadonlyArray<{ name?: string | null }>): string {
  // ⚠️ THE SINGLE-CANDIDATE CASE IS THE RAW `name`, NOT `getCandidateName`. A candidate with no
  // name reads "Add  to Bullhorn job" upstream, where every other screen would say
  // "Candidate 22719". Substituting the fallback here would be a fix, not a port.
  if (checked.length === 0) return 'candidates'
  if (checked.length === 1) return checked[0]?.name ?? ''
  return `${String(checked.length)} candidates`
}

/**
 * `LOCKED_STATUS_BY_ATS` — ATSes whose create endpoint cannot set a stage. The dropdown is SHOWN
 * AND DISABLED rather than hidden, pinned to the one stage the ATS creates at, with a line of
 * copy explaining why. JobDiva is the other way round: no status concept at all, so the whole
 * section goes.
 *
 * - Mercury creates every shortlist at "Selected" (no initial-stage field, no stage-write endpoint).
 * - RedOakTech's `POST /application` has no stage field; "Submitted" is the add-to-job placeholder.
 *
 * ⚠️ THE STATUS LIST STILL CARRIES EVERY REAL STAGE for both, because the same catalog drives
 * status-filtered search — so the lock is the only thing standing between the user and a stage the
 * ATS would silently ignore.
 */
export const LOCKED_STATUS_BY_ATS: Readonly<Record<string, string>> = {
  Mercury: 'Selected',
  RedOakTech: 'Submitted',
}

/** `LOCKED_STATUS_BY_ATS[atsName]` — undefined for every ATS whose dropdown is free. */
export function lockedAddToJobStatus(atsName: string | undefined): string | undefined {
  return atsName === undefined ? undefined : LOCKED_STATUS_BY_ATS[atsName]
}

export interface AddToJobPanelProps {
  atsOptions?: AddToJobAtsOptions
  /** `entityCheckboxContainer.checkedEntities` — one entry on the profile, many on review. */
  checked?: ReadonlyArray<{ id: number; name?: string | null }>
  /** Undefined until both GETs settle; the panel shows a spinner in the form's place. */
  statuses?: readonly AtsJobStatus[]
  defaultJob?: AtsJob | null
  defaultStatus?: string | null
  /** `ctrl.error` — the request failed for a reason the panel cannot name. */
  otherError?: boolean
  /** `flex-basis` on the component element; Extended at both call sites. */
  flexBasis?: number
  onClose?: () => void
  onSearchJobs?: (query: string) => Promise<AtsJob[]>
  onSubmit?: (values: { job: AtsJob; status: string }) => void
  /**
   * ⚠️ A WRITE WITH NO VISIBLE OUTCOME IS WORSE THAN NO WRITE. This one reaches the customer's
   * ATS, so the panel has to be able to say it failed and to stop a second press while the
   * first is in flight — `addToAtsJobAsync` is deduped server-side, but a double submit still
   * means two requests and two chances to confuse the user about what happened.
   */
  error?: string
  submitting?: boolean
}

export function AddToJobPanel({
  atsOptions = {},
  checked = [],
  statuses,
  defaultJob,
  defaultStatus,
  otherError = false,
  flexBasis,
  onClose,
  onSearchJobs,
  onSubmit,
  error,
  submitting = false,
}: AddToJobPanelProps): ReactElement {
  const isJobDiva = atsOptions.atsName === 'JobDiva'
  const lockedStatusText = lockedAddToJobStatus(atsOptions.atsName)
  const statusLocked = lockedStatusText !== undefined

  const [job, setJob] = useState<AtsJob | undefined>(defaultJob ?? undefined)
  const [jobText, setJobText] = useState(jobDisplayText(defaultJob, atsOptions))
  const [suggestions, setSuggestions] = useState<readonly AtsJob[]>([])
  const [selectedStatus, setSelectedStatus] = useState<string>(() => {
    if (lockedStatusText !== undefined) return lockedStatusText
    return defaultStatus ?? ''
  })
  const [submitted, setSubmitted] = useState(false)

  // `ng-show="!ctrl.form"` — the form does not exist until `$onInit` has both responses.
  const loaded = statuses !== undefined

  const jobError = submitted && job === undefined
  const statusError = submitted && !isJobDiva && selectedStatus === ''
  const noneChecked = submitted && checked.length === 0

  const content = (
    <div className="extension-candidate-add-to-job-container">
      <div className="extension-candidate-add-to-job-header">
        <div className="extension-candidate-add-to-job-x-icon">
          <i
            className="far fa-times fa-lg"
            role="button"
            aria-label="Close"
            tabIndex={0}
            onClick={onClose}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') onClose?.()
            }}
          />
        </div>
        <div className="extension-candidate-add-to-job-cta">
          {/* ⚠️ ONE EXPRESSION, NOT THREE CHILDREN. Written as `Add {name} to `, JSX emits three
              adjacent text nodes where AngularJS interpolation emits one, and the browser
              accumulates their advance widths slightly differently — "to" landed 1px right of
              where production has it. The `<br>` is upstream's and is load-bearing: "Bullhorn
              job" is always on its own line, however short the name above it. */}
          {`Add ${addToJobCtaText(checked)} to `}
          <br />
          <b>{`${atsOptions.atsLabel ?? atsOptions.atsName ?? ''} job`}</b>
        </div>
      </div>
      {loaded ? null : (
        <div className="add-to-job-form-loading">
          <i className="fa fa-spinner fa-spin fa-lg" />
        </div>
      )}
      {!loaded ? null : (
        <div className="extension-candidate-add-to-job-ats-form">
          <div className="extension-candidate-form-add-to-job-container">
            <div className="extension-candidate-add-to-job-form-job">
              <label className="bold-soft extension-candidate-add-to-job-label" htmlFor="add-to-job-job">
                Job
              </label>
              <label className="jobs-hourly-basis-label" htmlFor="add-to-job-job">
                Jobs are being updated on an hourly basis
              </label>
              <input
                id="add-to-job-job"
                name="job"
                className={`form-control input extension-candidate-add-to-job-input${jobError ? ' add-to-job-form-error' : ''}`}
                type="text"
                placeholder="Search for a job"
                autoComplete="off"
                value={jobText}
                onChange={(event) => {
                  setJobText(event.target.value)
                  // `onJobManualInput` — typing over a chosen job CLEARS ITS ID rather than the
                  // job, so the form fails `required` until something is picked from the list
                  // again. `typeahead-editable="false"` is the other half of the same rule.
                  setJob(undefined)
                  const query = event.target.value
                  if (onSearchJobs === undefined || query === '') {
                    setSuggestions([])
                    return
                  }
                  void onSearchJobs(query).then(setSuggestions, () => {
                    setSuggestions([])
                  })
                }}
              />
              {suggestions.length === 0 ? null : (
                <ul className="dropdown-menu" style={{ display: 'block' }} role="listbox">
                  {suggestions.map((suggestion) => (
                    <li key={suggestion.id}>
                      <a
                        tabIndex={-1}
                        href="#"
                        onClick={(event) => {
                          event.preventDefault()
                          setJob(suggestion)
                          setJobText(jobDisplayText(suggestion, atsOptions))
                          setSuggestions([])
                        }}
                      >
                        <div
                          className="add-to-ats-job-autocomplete-text"
                          title={jobAutocompleteText(suggestion, atsOptions)}
                        >
                          {jobAutocompleteText(suggestion, atsOptions)}
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
              {!jobError ? null : (
                <label className="control-label has-error extension-candidate-add-to-job-error-label">
                  Please select a job from the list
                </label>
              )}
            </div>
            {isJobDiva ? null : (
              <div className="extension-candidate-add-to-job-form-status mT20">
                <label className="bold-soft extension-candidate-add-to-job-label" htmlFor="add-to-job-status">
                  Status
                </label>
                <div className="select">
                  {/* ⚠️ THE PLACEHOLDER IS A DISABLED OPTION WITH AN EMPTY VALUE, and the grey
                      it renders in comes from `select:invalid` — a `required` select whose
                      value is "" is invalid, which is what colours "Select" #b2b7b8. Replacing
                      it with a `placeholder` attribute, which selects do not have, loses both. */}
                  <select
                    id="add-to-job-status"
                    name="status"
                    className={`form-control input extension-candidate-add-to-job-input${statusError ? ' add-to-job-form-error' : ''}`}
                    value={selectedStatus}
                    disabled={statusLocked}
                    required
                    onChange={(event) => {
                      setSelectedStatus(event.target.value)
                    }}
                  >
                    <option value="" disabled>
                      Select
                    </option>
                    {statuses.map((item) => (
                      <option key={item.status} value={item.status}>
                        {item.statusText}
                      </option>
                    ))}
                  </select>
                </div>
                {!statusLocked ? null : (
                  // The curly quotes are upstream's, and they are what is on screen.
                  //
                  // ⚠️ `{{ctrl.atsName}}`, NOT THE ATS LABEL. This line reached upstream's master
                  // after the label sweep (5cfd3ef899) and was never relabelled — it is the one
                  // template left that displays `atsName`. Kept verbatim; the fix is upstream's.
                  <div className="extension-candidate-add-to-job-label">
                    {`Candidates are added at the “${lockedStatusText}” stage in ${atsOptions.atsName ?? ''}.`}
                  </div>
                )}
                {!statusError ? null : (
                  <label className="control-label has-error extension-candidate-add-to-job-error-label">
                    Please select a status
                  </label>
                )}
              </div>
            )}
            {!noneChecked ? null : (
              <div className="extension-candidate-add-to-job-form-checked-candidates mT20">
                <label
                  className="control-label has-error extension-candidate-add-to-job-error-label txtcenter"
                  style={{ width: '100%' }}
                >
                  Please select at least one candidate
                </label>
              </div>
            )}
            {!otherError ? null : (
              <div className="extension-candidate-add-to-job-form-other-error mT20">
                <label
                  className="control-label has-error extension-candidate-add-to-job-error-label txtcenter"
                  style={{ width: '100%' }}
                >
                  Something went wrong, please try again.
                </label>
              </div>
            )}
            {/* The server's own words where it gave any — see `outwardWriteOutcome`. */}
            {error === undefined ? null : (
              <div className="add-to-ats-job-error text-danger" role="alert">
                {error}
              </div>
            )}
            <div className="add-to-ats-job-button pull-right mT20">
              <button
                type="button"
                disabled={submitting}
                onClick={() => {
                  setSubmitted(true)
                  if (job === undefined || checked.length === 0) return
                  if (!isJobDiva && selectedStatus === '') return
                  onSubmit?.({ job, status: selectedStatus })
                }}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )

  // The width lands on the ELEMENT, as on every other side view in this app — the state binds
  // `ng-style="{'flex-basis': maxWidth}"` to `<extension-alert-candidates-add-to-job>` and
  // nothing in the stylesheet mentions a width at all.
  return createElement(
    'extension-alert-candidates-add-to-job',
    flexBasis === undefined ? null : { style: { flexBasis: `${String(flexBasis)}px` } },
    content,
  )
}
